import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RefreshCcw, DollarSign, TrendingUp, Users, CheckCircle, FileText, Trash2,
} from 'lucide-react';
import TableColumnHeaderFilter from '../../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../../shared/utils/columnHeaderFilters';
import {
  CrmModalFormTabs,
  CrmModalFormBody,
  CrmModalFormPanel,
  CrmModalFieldGrid,
} from './CrmModalFormTabs';
import localApiAdapter from '../../../core/services/localApiAdapter';
import CustomSelect from '../../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../../shared/components/custom/CustomDatePicker';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../../products/saudi/components/QawaemErpListPageShell';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../../products/saudi/components/QawaemErpCrudModal';
import QawaemClickableStatusBadge from '../../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../../products/saudi/components/QawaemTableHeaderActionsCell';
import { partitionStatusFromColumns } from '../../../shared/utils/erpListColumnOrder';
import { enrichStatusOptions, getCrmModalTabCount, resolveCrmModalTabs, useCrmEntityReference } from '../../../shared/utils/crmPageUtils';
import { useCrmConfig } from '../useCrmConfig';
import { getCrmValueTierLabel, isCrmAutoReferenceEnabled, resolveCrmListViewMode } from '../crmConfigDefaults';
import { filterCrmRecordsByDateRange, sliceCrmListItems } from '../crmListPageUtils';
import { useSaudiListDateRange } from '../../../products/saudi/hooks/useSaudiListDateRange';
import { useSettings } from '../../../core/contexts/SettingsContext';
import { computeLeadScore, getLeadScoreTier, LEAD_SCORE_TIER_LABELS_AR } from '../../../shared/utils/leadScoring';
import useModuleConfigTheme from '../../../shared/hooks/useModuleConfigTheme';
import QawaemTableEmptyRow from '../../../shared/components/table/QawaemTableEmptyRow';
import CrmEntityReferenceField from './CrmEntityReferenceField';
import { exportToCSV } from '../../../shared/utils/importExportUtils';
import {
  formatCrmDate,
  formatCrmMoney,
  formatCrmPhone,
  getCrmPhonePlaceholder,
} from '../utils/crmSaudiFormat';
import {
  isBrandPhoneValid,
  normalizeBrandPhone,
} from '../../../shared/utils/brandCountry';
import { getBrandPriceTierThresholds, formatBrandMoney } from '../../../shared/utils/brandCurrency';
import { AppNotificationToast, useAppNotificationToast } from '../../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../../shared/utils/listSortUtils';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../../products/saudi/components/QawaemTableSelection';
import { QawaemTableStickyActionsTd } from '../../../products/saudi/components/QawaemTableStickyActions';
import { dispatchCrmApiRefresh, CRM_REFRESH_BY_API } from '../crmRefreshEvents';

/**
 * Configurable CRM entity list (leads, contacts, opportunities).
 * @param {object} config
 */
export default function CrmCoreListPage({
  pageKey,
  apiPath,
  icon: Icon,
  emptyIcon: EmptyIcon = Icon,
  fields,
  columns,
  statusOptions = [],
  sourceOptions = [],
  stageOptions = [],
  showValueTier = false,
  mapRecord = (r) => r,
  buildPayload = (form) => form,
  validateForm,
  formTabs: formTabsProp,
  entityRef = null,
}) {
  const { t, i18n } = useTranslation();
  const cfgTheme = useModuleConfigTheme();
  const p = (key, opts) => t(`modules.crm.pages.${pageKey}.${key}`, opts);
  const ps = (key, opts) => t(`modules.crm.pages.shared.${key}`, opts);
  const phonePlaceholder = getCrmPhonePlaceholder();
  const priceTiers = getBrandPriceTierThresholds();
  const crmConfig = useCrmConfig();
  const { dateRange } = useSaudiListDateRange();
  const { settings } = useSettings();
  const dateFieldKeys = useMemo(() => {
    const keys = columns.filter((c) => c.type === 'date').map((c) => c.key);
    return keys.length ? keys : ['created_at', 'updated_at'];
  }, [columns]);
  const exportRef = useRef(null);
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: entityLabel });

  const emptyForm = useMemo(() => {
    const base = {};
    fields.forEach((f) => { base[f.name] = f.defaultValue ?? ''; });
    if (entityRef?.field) base[entityRef.field] = '';
    return base;
  }, [fields, entityRef]);

  const [records, setRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  useEffect(() => {
    setViewMode(resolveCrmListViewMode(crmConfig.defaultView));
  }, [crmConfig.defaultView]);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [exportOpen, setExportOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(columns.map((c) => [c.key, true]))
  );
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formTab, setFormTab] = useState('general');

  const generateRef = useCrmEntityReference(
    entityRef?.prefixKey,
    entityRef?.fallback,
    records,
    entityRef?.field,
  );

  const fieldCount = fields.length;
  const allowedTabCount = getCrmModalTabCount(fieldCount);

  const formTabs = useMemo(() => {
    if (allowedTabCount === 0) return [];
    if (formTabsProp?.length) return resolveCrmModalTabs(fieldCount, formTabsProp);
    const hasNotes = fields.some((f) => f.type === 'textarea');
    if (allowedTabCount === 2 && hasNotes) {
      return [
        { id: 'general', label: p('tab_general', { defaultValue: 'عام' }), icon: Icon },
        { id: 'notes', label: p('tab_notes', { defaultValue: 'ملاحظات' }), icon: FileText, iconClassName: 'text-purple-400' },
      ];
    }
    return [{ id: 'general', label: p('tab_general', { defaultValue: 'عام' }), icon: Icon }];
  }, [allowedTabCount, formTabsProp, fieldCount, fields, p, Icon]);

  const defaultFormTabId = formTabs[0]?.id || 'general';

  const fieldsForTab = (tabId) => {
    if (!formTabs.length) return fields;
    const lastTabId = formTabs[formTabs.length - 1]?.id;
    return fields.filter((f) => {
      const fieldTab = f.tab || (f.type === 'textarea' ? 'notes' : 'general');
      if (fieldTab === tabId) return true;
      if (!formTabsProp?.length && f.type === 'textarea' && tabId === 'notes') return true;
      if (!f.tab && tabId === defaultFormTabId) return true;
      if (formTabsProp?.length && !formTabs.some((t) => t.id === fieldTab) && tabId === lastTabId) return true;
      return false;
    });
  };

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await localApiAdapter.get(apiPath);
      const rows = Array.isArray(res) ? res : (res?.data || []);
      setRecords(sortRecordsNewestFirst(rows.map(mapRecord)));
    } catch (err) {
      console.error(`CRM load error (${apiPath}):`, err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [apiPath, mapRecord]);

  useEffect(() => { void loadRecords(); }, [loadRecords]);

  useEffect(() => {
    if (!CRM_REFRESH_BY_API[apiPath]) return undefined;
    const handler = () => { void loadRecords(); };
    window.addEventListener(CRM_REFRESH_BY_API[apiPath], handler);
    return () => window.removeEventListener(CRM_REFRESH_BY_API[apiPath], handler);
  }, [apiPath, loadRecords]);

  const dateFiltered = useMemo(
    () => filterCrmRecordsByDateRange(records, dateRange, dateFieldKeys),
    [records, dateRange, dateFieldKeys],
  );

  const searchFiltered = useMemo(() => {
    if (viewMode !== 'cards' || !searchTerm) return dateFiltered;
    const q = searchTerm.toLowerCase();
    return dateFiltered.filter((r) =>
      columns.some((col) => {
        const v = col.derive ? col.derive(r) : r[col.key];
        return v != null && String(v).toLowerCase().includes(q);
      })
    );
  }, [dateFiltered, searchTerm, viewMode, columns]);

  const totalValue = showValueTier && crmConfig.showRevenue
    ? searchFiltered.reduce((s, r) => s + (parseFloat(r.value ?? r.estimated_value) || 0), 0)
    : 0;

  const statCards = useMemo(() => {
    const cards = [
      { key: 'total', label: p('stat_total'), value: searchFiltered.length, icon: Icon },
    ];
    if (showValueTier && crmConfig.showRevenue) {
      cards.push(
        { key: 'pipeline', label: p('stat_pipeline_value'), value: formatCrmMoney(totalValue), icon: DollarSign, variant: 'forest' },
        { key: 'new', label: p('status_new') || 'جديد', value: searchFiltered.filter((r) => r.status === 'new').length, icon: TrendingUp },
        { key: 'qualified', label: p('status_qualified') || 'مؤهل', value: searchFiltered.filter((r) => r.status === 'qualified').length, icon: CheckCircle, variant: 'forest' },
      );
    } else {
      const opts = stageOptions.length ? stageOptions : statusOptions;
      opts.slice(0, 3).forEach((opt, i) => {
        cards.push({
          key: opt.value,
          label: opt.label,
          value: searchFiltered.filter((r) => r.status === opt.value || r.stage === opt.value).length,
          icon: i === 0 ? Users : CheckCircle,
          variant: i % 2 ? 'forest' : 'copper',
        });
      });
    }
    return sliceQawaemStats(cards, 4);
  }, [searchFiltered, totalValue, showValueTier, crmConfig.showRevenue, p, Icon, stageOptions, statusOptions]);

  const { dataColumns, statusColumn } = useMemo(
    () => partitionStatusFromColumns(columns),
    [columns],
  );

  const statusField = statusColumn?.key || 'status';

  const badgeStatusOptions = useMemo(
    () => enrichStatusOptions(stageOptions.length ? stageOptions : statusOptions),
    [stageOptions, statusOptions],
  );

  const qawaemColumnDefs = useMemo(() => {
    const defs = dataColumns.map((col) => ({
      key: col.key,
      label: p(col.labelKey),
      checked: columnVisibility[col.key] !== false,
    }));
    if (statusColumn) {
      defs.push({
        key: statusColumn.key,
        label: p(statusColumn.labelKey),
        checked: columnVisibility[statusColumn.key] !== false,
      });
    }
    return defs;
  }, [dataColumns, statusColumn, columnVisibility, p]);

  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;

  const visibleDataColumns = useMemo(
    () => dataColumns.filter((c) => columnVisibility[c.key] !== false),
    [dataColumns, columnVisibility],
  );

  const showStatusColumn = statusColumn && columnVisibility[statusColumn.key] !== false;

  const tableColSpan = visibleDataColumns.length + (showStatusColumn ? 1 : 0) + 2;

  const getRecordStatus = (record) => record[statusField] ?? record.status ?? record.stage ?? '';

  const valueTierLabel = (amount, record) => {
    if (crmConfig.leadScoring && record && pageKey === 'leadsPage') {
      const tier = getLeadScoreTier(computeLeadScore(record, crmConfig));
      return LEAD_SCORE_TIER_LABELS_AR[tier] || tier;
    }
    return getCrmValueTierLabel(amount, crmConfig, {
      high: p('tier_high', { amount: formatBrandMoney(crmConfig.highValueLeadThreshold, false) }),
      medium: p('tier_medium', {
        low: formatBrandMoney(crmConfig.standardCustomerThreshold, false),
        high: formatBrandMoney(crmConfig.highValueLeadThreshold, false),
      }),
      low: p('tier_low', { amount: formatBrandMoney(crmConfig.standardCustomerThreshold, false) }),
    });
  };

  const getColumnFilterValue = (record, col) => {
    const raw = col.derive ? col.derive(record) : record[col.key];
    if (col.type === 'money') return formatCrmMoney(raw);
    if (col.type === 'date') return formatCrmDate(raw, i18n.language);
    if (col.type === 'phone') return formatCrmPhone(raw);
    if (col.type === 'status') {
      const opt = [...statusOptions, ...stageOptions].find((o) => o.value === raw);
      return opt?.label || raw || '';
    }
    if (col.type === 'tier') return valueTierLabel(raw, record);
    if (col.key === statusField) return String(getRecordStatus(record) ?? '');
    return String(raw ?? '');
  };

  const columnFilterRules = useMemo(() => {
    const cols = [...visibleDataColumns];
    if (showStatusColumn && statusColumn) cols.push(statusColumn);
    return cols.map((col) => {
      const getValue = (row) => getColumnFilterValue(row, col);
      if (col.type === 'status' || col.key === statusField) {
        return exactColumnRule(col.key, getValue);
      }
      return textColumnRule(col.key, getValue);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleDataColumns, showStatusColumn, statusColumn, statusField, statusOptions, stageOptions, i18n.language, priceTiers]);

  const filtered = useMemo(
    () => applyColumnFilters(searchFiltered, colFilters, columnFilterRules),
    [searchFiltered, colFilters, columnFilterRules],
  );

  const pagedFiltered = useMemo(
    () => sliceCrmListItems(filtered, crmConfig.itemsPerPage),
    [filtered, crmConfig.itemsPerPage],
  );

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filtered);

  useEffect(() => {
    if (selectedRecords.length > 0) setEditingKey(null);
  }, [selectedRecords, setEditingKey]);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`${apiPath}/${recordId}`, { [statusField]: newStatus });
      dispatchCrmApiRefresh(apiPath);
      loadRecords();
    } catch (err) {
      console.error(`CRM status update error (${apiPath}):`, err);
      alert(p('save_error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('delete_confirm'))) return;
    try {
      await localApiAdapter.delete(`${apiPath}/${id}`);
      dispatchCrmApiRefresh(apiPath);
      notifyDeleted(showNotify, entityLabel);
      loadRecords();
    } catch (err) {
      console.error(`CRM delete error (${apiPath}):`, err);
      notifyError(showNotify, p('save_error'));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRecords.length) return;
    if (!window.confirm(p('delete_confirm'))) return;
    try {
      const deleteCount = selectedRecords.length;
      await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`${apiPath}/${id}`)));
      dispatchCrmApiRefresh(apiPath);
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
      setSelectedRecords([]);
      loadRecords();
    } catch (err) {
      console.error(`CRM bulk delete error (${apiPath}):`, err);
      notifyError(showNotify, p('save_error'));
    }
  };

  const closeModal = () => {
    setFormTab(defaultFormTabId);
    setShowModal(false);
  };

  const openNew = () => {
    setEditing(null);
    const next = { ...emptyForm };
    if (entityRef?.field) next[entityRef.field] = generateRef();
    setFormData(next);
    setFormTab(defaultFormTabId);
    setShowModal(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    const next = { ...emptyForm };
    fields.forEach((f) => {
      const raw = record[f.name] ?? record[f.apiName || f.name] ?? '';
      next[f.name] = f.type === 'select' && raw !== '' && raw != null ? String(raw) : raw;
    });
    if (entityRef?.field) {
      next[entityRef.field] = record[entityRef.field] ?? '';
    }
    setFormData(next);
    setFormTab(defaultFormTabId);
    setShowModal(true);
  };

  const openView = (record) => {
    setViewingRecord(record);
    setShowViewModal(true);
  };

  const closeView = () => {
    setShowViewModal(false);
    setViewingRecord(null);
  };

  const renderFormField = (field) => (
    <div key={field.name} className={field.fullWidth || field.type === 'textarea' ? 'md:col-span-3' : ''}>
      <label className={cfgTheme.label}>
        {p(field.labelKey)}
        {field.required ? <span className="text-red-600 font-bold"> *</span> : null}
      </label>
      {field.type === 'select' ? (
        <CustomSelect
          value={formData[field.name]}
          onChange={(v) => setFormData({ ...formData, [field.name]: v })}
          options={field.options || []}
          placeholder={p(field.placeholderKey || 'select')}
        />
      ) : field.inputType === 'date' ? (
        <CustomDatePicker
          value={formData[field.name]}
          onChange={(v) => setFormData({ ...formData, [field.name]: v })}
          showTime={false}
        />
      ) : field.type === 'textarea' ? (
        <textarea
          value={formData[field.name]}
          onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          rows={3}
          className={`${cfgTheme.input} resize-none`}
        />
      ) : (
        <input
          type={field.inputType || 'text'}
          required={field.required}
          value={formData[field.name]}
          onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          placeholder={field.name === 'phone' ? phonePlaceholder : (field.placeholderKey ? p(field.placeholderKey) : '')}
          className={cfgTheme.input}
        />
      )}
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.phone && !isBrandPhoneValid(formData.phone)) {
      alert(p('alert_invalid_phone'));
      return;
    }
    if (validateForm?.(formData, p)) return;
    try {
      const payload = buildPayload({
        ...formData,
        phone: normalizeBrandPhone(formData.phone),
      });
      if (entityRef?.field) {
        const autoRef = isCrmAutoReferenceEnabled(settings, entityRef.prefixKey);
        payload[entityRef.field] = (formData[entityRef.field] || '').trim() || (autoRef ? generateRef() : '');
      }
      if (editing?.id) {
        await localApiAdapter.put(`${apiPath}/${editing.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post(apiPath, payload);
        notifyCreated(showNotify, entityLabel);
      }
      dispatchCrmApiRefresh(apiPath);
      closeModal();
      loadRecords();
    } catch (err) {
      console.error(`CRM save error (${apiPath}):`, err);
      notifyError(showNotify, p('save_error'));
    }
  };

  const renderCell = (record, col) => {
    const raw = col.derive ? col.derive(record) : record[col.key];
    if (col.type === 'money') return formatCrmMoney(raw);
    if (col.type === 'date') return formatCrmDate(raw, i18n.language);
    if (col.type === 'phone') return formatCrmPhone(raw);
    if (col.type === 'status') {
      const opt = [...statusOptions, ...stageOptions].find((o) => o.value === raw);
      return opt?.label || raw || '—';
    }
    if (col.type === 'tier') return valueTierLabel(raw, record);
    return raw ?? '—';
  };

  const exportColumns = useMemo(() => {
    const cols = [...visibleDataColumns];
    if (showStatusColumn && statusColumn) cols.push(statusColumn);
    return cols;
  }, [visibleDataColumns, showStatusColumn, statusColumn]);

  const handleExportCsv = () => {
    const headers = exportColumns.map((col) => ({ key: col.key, label: p(col.labelKey) }));
    const rows = filtered.map((record) => {
      const row = {};
      exportColumns.forEach((col) => {
        row[col.key] = renderCell(record, col);
      });
      return row;
    });
    exportToCSV(rows, headers, `${pageKey}-${new Date().toISOString().split('T')[0]}.csv`);
    setExportOpen(false);
  };

  const statusFilterOptions = useMemo(
    () => badgeStatusOptions.map((opt) => ({ value: opt.value, label: opt.label })),
    [badgeStatusOptions],
  );

  return (
    <>
      <QawaemErpListPageShell
        className=""
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Icon}
        stats={statCards}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={selectedRecords.length > 0 ? (
          <button
            type="button"
            onClick={handleBulkDelete}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
            title={t('common.delete')}
          >
            <Trash2 size={16} />
            <span>{t('common.delete')} ({selectedRecords.length})</span>
          </button>
        ) : null}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder')}
        columns={qawaemColumnDefs}
        visibleColumnCount={exportColumns.length}
        totalColumnCount={dataColumns.length + (statusColumn ? 1 : 0)}
        onCreate={openNew}
        createLabel={p('add')}
        headerActions={(
          <button
            type="button"
            onClick={loadRecords}
            className="refresh-button"
            aria-label={p('refresh')}
            title={p('refresh')}
          >
            <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
        )}
        importExport={{
          importOpen: false,
          exportOpen,
          onToggleImport: () => {},
          onToggleExport: () => setExportOpen((v) => !v),
          importRef: exportRef,
          exportRef,
          importLabel: p('import_label') || t('common.import', { defaultValue: 'استيراد' }),
          exportLabel: p('export_label') || t('common.export', { defaultValue: 'تصدير' }),
          generatingLabel: t('common.loading'),
          isGeneratingPDF: false,
          onImportCSV: () => {},
          onImportExcel: () => {},
          onExportPDF: () => {},
          onExportExcel: () => {},
          onExportCSV: handleExportCsv,
        }}
      >
        {viewMode === 'table' ? (
          <div className="glass-table-scroll overflow-x-auto" data-testid={`crm-${pageKey}-page`}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {visibleDataColumns.map((col) => (
                    <TableColumnHeaderFilter
                      key={col.key}
                      columnKey={col.key}
                      label={p(col.labelKey)}
                      kind={col.type === 'status' ? 'select' : 'text'}
                      value={colFilters[col.key] ?? ''}
                      onChange={(v) => setColFilter(col.key, v)}
                      options={col.type === 'status' ? statusFilterOptions : undefined}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white font-semibold"
                      className="px-6 py-4"
                    />
                  ))}
                  {showStatusColumn ? (
                    <TableColumnHeaderFilter
                      columnKey={statusColumn.key}
                      label={p(statusColumn.labelKey)}
                      kind="select"
                      value={colFilters[statusColumn.key] ?? ''}
                      onChange={(v) => setColFilter(statusColumn.key, v)}
                      options={statusFilterOptions}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      align="center"
                      thClassName="text-white font-semibold"
                    />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {loading ? (
                  <tr>
                    <td colSpan={tableColSpan} className="qawaem-table-empty-cell p-12 text-center text-white/60">{p('loading')}</td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <QawaemTableEmptyRow
                    colSpan={tableColSpan}
                    icon={EmptyIcon}
                    message={p('empty_state')}
                  />
                ) : pagedFiltered.map((record) => (
                  <tr key={record.id} className={`hover:bg-white/5 ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                    <QawaemTableSelectRowCell
                      checked={selectedRecords.includes(record.id)}
                      onChange={() => handleSelectRecord(record.id)}
                    />
                    {visibleDataColumns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-6 py-4 text-sm text-white/90 ${entityRef?.field && col.key === entityRef.field ? 'font-mono text-xs' : ''}`}
                      >
                        {renderCell(record, col)}
                      </td>
                    ))}
                    {showStatusColumn ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={getRecordStatus(record)}
                            recordId={record.id}
                            options={badgeStatusOptions}
                            onChange={handleStatusChange}
                            title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                        </div>
                      </td>
                    ) : null}
                    <QawaemTableStickyActionsTd>
                      <QawaemErpRowActions
                        onView={() => openView(record)}
                        onEdit={() => openEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </QawaemTableStickyActionsTd>
                  </tr>
                ))}
                {showValueTier && crmConfig.showRevenue && !loading && filtered.length > 0 && (
                  <tr className="bg-white/5">
                    <td colSpan={tableColSpan} className="px-6 py-3 text-sm text-right text-white/80">
                      {p('footer_total_value')}: <strong className="text-white">{formatCrmMoney(totalValue)}</strong>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : loading ? (
          <div className="p-12 text-center text-white/60">{p('loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <EmptyIcon className="mx-auto mb-4 opacity-30 h-12 w-12" />
            <p className="text-white/60">{p('empty_state')}</p>
          </div>
        ) : (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
            {pagedFiltered.map((record) => (
              <div key={record.id} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 h-full min-w-0">
                <div className="flex justify-between items-start mb-3">
                  <p className="text-white font-semibold min-w-0">{record.name || record.title || record.lead_ref || record.contact_ref || '—'}</p>
                  <QawaemErpRowActions
                    compact
                    size={14}
                    className="shrink-0 ms-2"
                    onView={() => openView(record)}
                    onEdit={() => openEdit(record)}
                    onDelete={() => handleDelete(record.id)}
                    viewTitle={t('common.view')}
                    editTitle={t('common.edit')}
                    deleteTitle={t('common.delete')}
                  />
                </div>
                {showStatusColumn ? (
                  <div className="mb-2">
                    <QawaemClickableStatusBadge
                      status={getRecordStatus(record)}
                      recordId={record.id}
                      options={badgeStatusOptions}
                      onChange={handleStatusChange}
                    />
                  </div>
                ) : null}
                {visibleDataColumns.slice(0, 4).map((col) => (
                  <p key={col.key} className="text-white/60 text-sm">
                    <span className="text-white/40">{p(col.labelKey)}: </span>
                    {renderCell(record, col)}
                  </p>
                ))}
              </div>
            ))}
          </div>
        )}
      </QawaemErpListPageShell>

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={closeModal}
          title={editing ? p('modal_edit') : p('modal_new')}
          icon={Icon}
          formId={`crm-${pageKey}-form`}
          saveLabel={t('common.save')}
          onSubmit
          maxWidth="1200px"
          testId={`crm-${pageKey}-modal`}
        >
          {formTabs.length > 0 ? (
            <CrmModalFormBody>
              <CrmModalFormTabs
                tabs={formTabs}
                activeTab={formTab}
                onTabChange={setFormTab}
                ariaLabel={editing ? p('modal_edit') : p('modal_new')}
              />
              <CrmModalFormPanel>
                <form id={`crm-${pageKey}-form`} onSubmit={handleSubmit}>
                  {formTabs.map((tab) => formTab === tab.id && (
                    <CrmModalFieldGrid key={tab.id}>
                      {entityRef?.field && tab.id === defaultFormTabId ? (
                        <CrmEntityReferenceField
                          value={formData[entityRef.field]}
                          onChange={(v) => setFormData({ ...formData, [entityRef.field]: v })}
                          onRegenerate={editing ? undefined : () => setFormData({ ...formData, [entityRef.field]: generateRef() })}
                          label={p('field_ref', { defaultValue: ps('field_code_required') })}
                          placeholder={p('placeholder_ref', { defaultValue: ps('placeholder_code') })}
                          autoGeneratedHint={editing ? undefined : ps('auto_generated')}
                          regenerateTitle={ps('regenerate_code')}
                          labelClassName={cfgTheme.label}
                          inputClassName={cfgTheme.input}
                        />
                      ) : null}
                      {fieldsForTab(tab.id).map(renderFormField)}
                    </CrmModalFieldGrid>
                  ))}
                </form>
              </CrmModalFormPanel>
            </CrmModalFormBody>
          ) : (
            <form id={`crm-${pageKey}-form`} onSubmit={handleSubmit}>
              <CrmModalFieldGrid>
                {entityRef?.field ? (
                  <CrmEntityReferenceField
                    value={formData[entityRef.field]}
                    onChange={(v) => setFormData({ ...formData, [entityRef.field]: v })}
                    onRegenerate={editing ? undefined : () => setFormData({ ...formData, [entityRef.field]: generateRef() })}
                    label={p('field_ref', { defaultValue: ps('field_code_required') })}
                    placeholder={p('placeholder_ref', { defaultValue: ps('placeholder_code') })}
                    autoGeneratedHint={editing ? undefined : ps('auto_generated')}
                    regenerateTitle={ps('regenerate_code')}
                    labelClassName={cfgTheme.label}
                    inputClassName={cfgTheme.input}
                  />
                ) : null}
                {fields.map(renderFormField)}
              </CrmModalFieldGrid>
            </form>
          )}
        </QawaemErpCrudModal>
      )}

      {showViewModal && viewingRecord ? (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={closeView}
          title={p('view_title')}
          icon={Icon}
          maxWidth="640px"
          testId={`crm-${pageKey}-view-modal`}
        >
          <dl className="space-y-3 text-sm">
            {columns.map((col) => (
              <div key={col.key} className={`flex justify-between gap-4 border-b pb-2 ${cfgTheme.light ? 'border-slate-100' : 'border-white/10'}`}>
                <dt className={cfgTheme.light ? 'text-slate-500' : 'text-white/60'}>{p(col.labelKey)}</dt>
                <dd className={`font-medium text-left ${cfgTheme.title}`}>{renderCell(viewingRecord, col)}</dd>
              </div>
            ))}
          </dl>
        </QawaemErpViewModal>
      ) : null}

      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
}
